/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { API_BASE, getAuthHeaders, setOpsToken } from "../api/client";
import { LoadingPulse } from "../ui";
import { opsUserFromAuthPayload } from "../lib/userDisplay";
import { mapOpsAuthError, throwAuthFailure } from "../lib/auth";
import { landingPageForUser } from "../routing/paths";
import { isDev } from "../lib/env";
import { DEMO_USERS } from "../lib/data/demoUsers";

export function AuthScreen({
  activeTheme,
  lang,
  authReady,
  authMode,
  setAuthMode,
  resetToken,
  setResetToken,
  onAuthenticated,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [registerName, setRegisterName] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [authNotice, setAuthNotice] = useState("");
  const googleBtnRef = useRef(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

  async function handleLogin(e) {
    e.preventDefault();
    setAuthError("");
    setAuthNotice("");
    setLoading(true);
    const emailValue = email.toLowerCase().trim();
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ email: emailValue, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throwAuthFailure(data, "Invalid credentials");
      const session = opsUserFromAuthPayload(data);
      if (!session) throw new Error("Invalid credentials");
      if (session.access_token) setOpsToken(session.access_token);
      const { access_token: _at, token_type: _tt, product: _p, status: _s, ...rest } = session;
      onAuthenticated(rest, landingPageForUser(rest));
      setAuthError("");
    } catch (err) {
      setAuthError(mapOpsAuthError(err, lang, "Invalid email or password. Please try again.", "Λάθος email ή κωδικός πρόσβασης"));
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    setAuthError("");
    setAuthNotice("");
    const emailValue = email.toLowerCase().trim();
    if (emailValue.endsWith("@stiq.gr")) {
      setAuthError(
        lang === "gr"
          ? "Οι λογαριασμοί @stiq.gr συνδέονται με Google, όχι με εγγραφή."
          : "@stiq.gr accounts must sign in with Google, not a password."
      );
      return;
    }
    if (password.length < 8) {
      setAuthError(lang === "gr" ? "Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες." : "Password must be at least 8 characters.");
      return;
    }
    if (password !== passwordConfirm) {
      setAuthError(lang === "gr" ? "Οι κωδικοί δεν ταιριάζουν." : "Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          email: emailValue,
          password,
          name: registerName.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throwAuthFailure(data, "Registration failed");
      setAuthMode("login");
      setPassword("");
      setPasswordConfirm("");
      setAuthNotice(
        lang === "gr"
          ? "Ο λογαριασμός δημιουργήθηκε. Περιμένει έγκριση διαχειριστή στο Identity (Stiq OS)."
          : "Account created. An administrator must approve you in Identity (Stiq OS) before you can sign in."
      );
    } catch (err) {
      setAuthError(mapOpsAuthError(err, lang, "Registration failed", "Η εγγραφή απέτυχε"));
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setAuthError("");
    setAuthNotice("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throwAuthFailure(data, "Request failed");
      setAuthNotice(
        lang === "gr"
          ? "Αν υπάρχει λογαριασμός, στάλθηκε email επαναφοράς."
          : "If an account exists, a reset email was sent."
      );
    } catch (err) {
      setAuthError(mapOpsAuthError(err, lang, "Request failed", "Το αίτημα απέτυχε"));
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    setAuthError("");
    setAuthNotice("");
    if (password.length < 8) {
      setAuthError(lang === "gr" ? "Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες." : "Password must be at least 8 characters.");
      return;
    }
    if (password !== passwordConfirm) {
      setAuthError(lang === "gr" ? "Οι κωδικοί δεν ταιριάζουν." : "Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ token: resetToken, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throwAuthFailure(data, "Reset failed");
      setPassword("");
      setPasswordConfirm("");
      setResetToken("");
      setAuthMode("login");
      setAuthNotice(
        lang === "gr"
          ? "Ο κωδικός ενημερώθηκε. Συνδεθείτε με email και κωδικό."
          : "Password updated. Sign in with email and password."
      );
    } catch (err) {
      setAuthError(mapOpsAuthError(err, lang, "This reset link is invalid or has expired.", "Ο σύνδεσμος επαναφοράς δεν ισχύει ή έχει λήξει."));
    } finally {
      setLoading(false);
    }
  }

  async function completeOpsGoogle(idToken) {
    setAuthError("");
    setAuthNotice("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/google`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ id_token: idToken, product: "ops" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throwAuthFailure(data, "Google sign-in failed");
      if (data.product === "os") {
        throwAuthFailure(
          {
            detail: {
              code: "NO_ACCESS_THIS_APP",
              message: "No access to Stiq Ops. Ask an administrator to grant Ops modules.",
            },
          },
          "No access to Stiq Ops."
        );
      }
      const session = opsUserFromAuthPayload(data);
      if (!session) throw new Error("Google sign-in failed");
      if (session.access_token) setOpsToken(session.access_token);
      const { access_token: _at, token_type: _tt, product: _p, status: _s, ...rest } = session;
      onAuthenticated(rest, landingPageForUser(rest));
    } catch (err) {
      setAuthError(mapOpsAuthError(err, lang, "Google sign-in failed", "Η σύνδεση Google απέτυχε"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!authReady) return undefined;
    if (authMode === "forgot" || authMode === "reset") return undefined;
    if (!googleClientId || !googleBtnRef.current) return undefined;
    const src = "https://accounts.google.com/gsi/client";
    let cancelled = false;

    function renderButton() {
      if (cancelled || !window.google || !googleBtnRef.current) return;
      try {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: (response) => {
            if (response && response.credential) {
              void completeOpsGoogle(response.credential);
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          ux_mode: "popup",
        });
        googleBtnRef.current.innerHTML = "";
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: "outline",
          size: "large",
          text: authMode === "register" ? "signup_with" : "signin_with",
          width: 280,
        });
      } catch {
        if (!cancelled) {
          setAuthError(
            lang === "gr"
              ? "Το Google Sign-In απέτυχε. Προσθέστε αυτό το origin στο Google Cloud."
              : "Google Sign-In failed. Add this origin to the OAuth client in Google Cloud."
          );
        }
      }
    }

    if (window.google && window.google.accounts && window.google.accounts.id) {
      renderButton();
      return () => {
        cancelled = true;
      };
    }
    let script = document.querySelector(`script[src="${src}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    function onLoaded() {
      script.setAttribute("data-loaded", "true");
      renderButton();
    }
    function onScriptError() {
      if (!cancelled) {
        setAuthError(
          lang === "gr"
            ? "Δεν φόρτωσε το Google Sign-In. Ελέγξτε το δίκτυο."
            : "Could not load Google Sign-In. Check your network and try again."
        );
      }
    }
    script.addEventListener("load", onLoaded);
    script.addEventListener("error", onScriptError);
    if (script.getAttribute("data-loaded") === "true") {
      renderButton();
    }
    return () => {
      cancelled = true;
      script.removeEventListener("load", onLoaded);
      script.removeEventListener("error", onScriptError);
    };
  }, [lang, authReady, authMode, googleClientId]);

  if (!authReady) {
    return (
      <>
          <div
          style={{
            minHeight: "100vh",
            background: "var(--bg)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
            padding: 16,
          }}
        >
          <img
            src={activeTheme.logoSrc}
            alt={activeTheme.name}
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              objectFit: "cover",
              display: "block",
              marginBottom: 12,
            }}
          />
          <LoadingPulse size={28} />
          <div style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 600 }}>Loading...</div>
        </div>
      </>
    );
  }


  return (
    <>
      <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div style={{ width: "100%", maxWidth: 400, margin: "0 auto" }}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 24, padding: "36px 28px", boxShadow: "0 8px 40px rgba(27,67,50,0.10)" }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <img
                src={activeTheme.logoSrc}
                alt={activeTheme.name}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  objectFit: "cover",
                  display: "block",
                  margin: "0 auto 12px",
                }}
              />
              <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 32, color: "var(--text-primary)", marginTop: 14, lineHeight: 1 }}>{activeTheme.name}</div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
                {authMode === "register"
                  ? (lang === "gr" ? "Εγγραφή προσωπικού (pending)" : "Staff registration — pending approval")
                  : authMode === "forgot"
                    ? (lang === "gr" ? "Επαναφορά κωδικού" : "Reset your password")
                    : authMode === "reset"
                      ? (lang === "gr" ? "Νέος κωδικός" : "Choose a new password")
                      : "Operations Platform"}
              </div>
            </div>

            {(authMode === "login" || authMode === "register") && (
              <>
                <div ref={googleBtnRef} style={{ display: "flex", justifyContent: "center", marginBottom: 16, minHeight: 40 }} />
                {googleClientId ? (
                  <div style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "center", marginTop: -8, marginBottom: 12 }}>
                    {lang === "gr"
                      ? "Google: @stiq.gr ή προσωπικό Gmail. Νέοι λογαριασμοί περιμένουν έγκριση."
                      : "Google: @stiq.gr or personal Gmail. New accounts wait for admin approval."}
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "center", marginBottom: 12 }}>
                    {lang === "gr"
                      ? "Το Google δεν είναι ρυθμισμένο (REACT_APP_GOOGLE_CLIENT_ID)."
                      : "Google sign-in is not configured (REACT_APP_GOOGLE_CLIENT_ID)."}
                  </div>
                )}
              </>
            )}

            <form
              onSubmit={
                authMode === "register"
                  ? handleRegister
                  : authMode === "forgot"
                    ? handleForgot
                    : authMode === "reset"
                      ? handleResetPassword
                      : handleLogin
              }
              style={{ display: "flex", flexDirection: "column", gap: 12 }}
            >
              {authMode === "register" ? (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{lang === "gr" ? "Όνομα" : "Name"}</label>
                <input
                  type="text"
                  className="form-input"
                  value={registerName}
                  onChange={e => setRegisterName(e.target.value)}
                  autoComplete="name"
                />
              </div>
              ) : null}
              {authMode !== "reset" ? (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={authMode === "register" ? "name@gmail.com" : `name@${activeTheme.emailDomain}`}
                  autoComplete="email"
                  required
                />
              </div>
              ) : null}

              {authMode !== "forgot" ? (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">
                  {authMode === "reset"
                    ? (lang === "gr" ? "Νέος κωδικός" : "New password")
                    : "Password"}
                </label>
                <input
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete={authMode === "login" ? "current-password" : "new-password"}
                  required={authMode !== "forgot"}
                  minLength={authMode === "login" ? undefined : 8}
                />
              </div>
              ) : null}

              {(authMode === "register" || authMode === "reset") ? (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{lang === "gr" ? "Επιβεβαίωση κωδικού" : "Confirm password"}</label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordConfirm}
                  onChange={e => setPasswordConfirm(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </div>
              ) : null}

              {authError && (
                <div style={{ fontSize: 13, color: "var(--danger)", fontWeight: 600, marginTop: -8, marginBottom: 8 }}>
                  {authError}
                </div>
              )}
              {authNotice && !authError && (
                <div style={{ fontSize: 13, color: "var(--green)", fontWeight: 600, marginTop: -8, marginBottom: 8 }}>
                  {authNotice}
                </div>
              )}

              <button
                type="submit"
                className="btn-base btn-green"
                disabled={loading}
                aria-busy={loading}
                style={{ width: "100%", justifyContent: "center" }}
              >
                {loading ? (
                  <span className="spinner" aria-label={lang === "gr" ? "Φόρτωση" : "Loading"} />
                ) : authMode === "register"
                  ? (lang === "gr" ? "Εγγραφή" : "Create account")
                  : authMode === "forgot"
                    ? (lang === "gr" ? "Αποστολή συνδέσμου" : "Send reset link")
                    : authMode === "reset"
                      ? (lang === "gr" ? "Αποθήκευση κωδικού" : "Save password")
                      : "Sign In"}
              </button>
            </form>
            {authMode === "login" ? (
              <button
                type="button"
                onClick={() => {
                  setAuthError("");
                  setAuthNotice("");
                  setAuthMode("forgot");
                }}
                style={{
                  marginTop: 8,
                  width: "100%",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 12,
                  color: "var(--text-muted)",
                  fontWeight: 600,
                }}
              >
                {lang === "gr" ? "Ξέχασα τον κωδικό" : "Forgot password?"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setAuthError("");
                setAuthNotice("");
                setAuthMode(m => (m === "login" ? "register" : "login"));
                setPasswordConfirm("");
              }}
              style={{
                marginTop: 4,
                width: "100%",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 12,
                color: "var(--text-muted)",
                fontWeight: 600,
              }}
            >
              {authMode === "login"
                ? (lang === "gr" ? "Νέος λογαριασμός (προσωπικό email)" : "Create account (personal email)")
                : (lang === "gr" ? "Έχω ήδη λογαριασμό" : "Already have an account? Sign in")}
            </button>
          </div>

          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 20, textAlign: "center" }}>
            Internal use only · STIQ Operations
          </div>

          {isDev && (
          <>
          {/* Demo accounts toggle */}
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button
              onClick={() => setShowDemo(v => !v)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 12,
                color: "var(--text-muted)",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "6px 12px",
                borderRadius: 20,
                transition: "color 0.15s",
              }}
            >
              {showDemo ? "▲" : "▼"}
              {lang === "gr" ? "Demo λογαριασμοί" : "Demo accounts"}
            </button>
          </div>

          {/* Demo accounts grid — only when expanded */}
          {showDemo && (
            <div style={{
              maxWidth: 400,
              width: "100%",
              margin: "8px auto 0",
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 20,
              padding: "16px 20px",
            }}>
              <div style={{
                fontSize: 10,
                fontWeight: 800,
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 12,
              }}>
                {lang === "gr" ? "Γρήγορη σύνδεση" : "Quick sign in"}
              </div>

              {[
                { role: "admin",         label: "Network Admin", labelGr: "Network Admin" },
                { role: "hr",            label: "HR",            labelGr: "HR" },
                { role: "store_manager", label: "Store Manager", labelGr: "Διευθυντής" },
                { role: "crew",          label: "Crew",          labelGr: "Crew" },
              ].map(group => {
                const groupUsers = DEMO_USERS.filter(u => u.role === group.role);
                if (groupUsers.length === 0) return null;
                return (
                  <div key={group.role} style={{ marginBottom: 12 }}>
                    <div style={{
                      fontSize: 9,
                      fontWeight: 800,
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      marginBottom: 6,
                    }}>
                      {lang === "gr" ? group.labelGr : group.label}
                    </div>
                    <div style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                    }}>
                      {groupUsers.map(u => (
                        <button
                          key={u.id}
                          type="button"
                          disabled={loading}
                          onClick={async () => {
                            setAuthError("");
                            setLoading(true);
                            try {
                              const res = await fetch(`${API_BASE}/auth/login`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  email: String(u.email || "").toLowerCase().trim(),
                                  password: u.password || "1234",
                                }),
                              });
                              if (!res.ok) {
                                setAuthError(
                                  lang === "gr"
                                    ? "Η γρήγορη σύνδεση απέτυχε — χρησιμοποιήστε email/κωδικό"
                                    : "Quick sign-in failed — use email and password"
                                );
                                return;
                              }
                              const data = await res.json();
                              if (data.access_token) {
                                setOpsToken(data.access_token);
                              }
                              const { access_token: _at, ...rest } = data;
                              onAuthenticated(rest, landingPageForUser(rest));
                            } catch {
                              setAuthError(
                                lang === "gr"
                                  ? "Η γρήγορη σύνδεση απέτυχε — χρησιμοποιήστε email/κωδικό"
                                  : "Quick sign-in failed — use email and password"
                              );
                            } finally {
                              setLoading(false);
                            }
                          }}
                          style={{
                            padding: "11px 13px",
                            borderRadius: 12,
                            border: "1.5px solid var(--border)",
                            background: "var(--surface2)",
                            cursor: loading ? "wait" : "pointer",
                            fontFamily: "DM Sans, sans-serif",
                            transition: "all 0.15s",
                            textAlign: "left",
                            minHeight: 44,
                            opacity: loading ? 0.7 : 1,
                          }}
                        >
                          <div style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "var(--text-primary)",
                          }}>
                            {u.name.split(" ")[0]}
                          </div>
                          <div style={{
                            fontSize: 10,
                            color: "var(--text-muted)",
                            marginTop: 1,
                          }}>
                            {u.store === "All Stores" ? "All Stores" : u.store}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </>
  );

}
